import { useEffect } from "react";
import { twMerge } from "tailwind-merge";
import AnimatedPortal from "./AnimatedPortal";

export type AnimatedModalProps = {
    visible: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
    /** Backdrop z-index wrapper. Default z-50. */
    zIndexClassName?: string;
    /** Close on Escape. Default true. */
    closeOnEscape?: boolean;
    /** Close on backdrop click. Default true. */
    closeOnBackdrop?: boolean;
};

/**
 * Center modal: backdrop fade + panel scale/opacity/translateY (iOS feel).
 * Does not own title/buttons — callers keep their content.
 */
const AnimatedModal: React.FC<AnimatedModalProps> = ({
    visible,
    onClose,
    children,
    className = "",
    zIndexClassName = "z-50",
    closeOnEscape = true,
    closeOnBackdrop = true,
}) => {
    useEffect(() => {
        if (!visible || !closeOnEscape) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [visible, closeOnEscape, onClose]);

    return (
        <AnimatedPortal visible={visible}>
            {(state) => (
                <div
                    className={twMerge(
                        "fixed inset-0 flex items-center justify-center px-4",
                        zIndexClassName
                    )}
                    role="presentation"
                    onClick={(e) => {
                        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
                    }}
                >
                    <div
                        className="ios-backdrop pointer-events-none"
                        data-state={state}
                        aria-hidden
                    />
                    <div
                        className={twMerge(
                            "ios-modal-panel z-10 max-h-[90vh] overflow-y-auto",
                            className
                        )}
                        data-state={state}
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                    </div>
                </div>
            )}
        </AnimatedPortal>
    );
};

export default AnimatedModal;
